<script setup lang="ts">
import { computed, ref, watch } from "vue";
import BRadios from "../common/BRadios.vue";
import ResourceImage from "../resource/resourceImage.vue";
import ResourceAutocompleteSelected from "../../components/resource/autocomplete/Selected.vue";
import ListItemResource from "../resource/autocomplete/ListItem.vue";
import CountryFlag from "../common/CountryFlag.vue";
import SelectCandidateSource from "./SelectCandidateSource.vue";
import BRadio from "@/components/common/bRadio.vue";
import TextInput from "@/components/common/textInput.vue";
import BButton from "@/components/common/bButton.vue";
import bLabel from "@/components/common/bLabel.vue";
import SearchImageOnGoogle from "@/components/common/SearchImageOnGoogle.vue";
import Cropper from "@/components/common/Cropper.vue";
import BModal from "@/components/common/BModal.vue";
import DefaultProfile from "@/assets/svg/profile.svg?inline";
import BChoiceOptions from "@/components/common/BChoiceOptions.vue";
import Skeleton from "@/components/common/skeleton.vue";
import IonMdWoman from "~icons/ion/md-woman";
import IonMdMan from "~icons/ion/md-man";
import SolarMapPointSchoolLineDuotone from "~icons/solar/map-point-school-line-duotone";
import SolarPenBroken from "~icons/solar/pen-broken";
import TargetSchoolIcon from "@/components/resource/degree/TargetSchoolIcon.vue";
import bCheckbox from "@/components/common/bCheckbox.vue";
import BAlert from "@/components/common/BAlert.vue";
import MaterialSymbolsVerifiedUserOutlineRounded from "~icons/material-symbols/verified-user-outline-rounded";
import { useInfinitySchool } from "@/services/school";
import { useInfinityJob } from "@/services/job";
import { useInfinityJobCategory } from "@/services/jobCategory";
import { useInfinityFormular } from "@/services/formular";
import { useInfinityOffice } from "@/services/office";
import { useInfinityTeam } from "@/services/team";
import { useInfinityRecruitmentTag } from "@/services/recruitmentTag";
import { useInfinityContractType } from "@/services/contractType";
import bSelect from "@/components/common/bSelect.vue";
import bDatePicker from "@/components/common/bDatePicker.vue";
import { recruitmentApi, resourceApi, userApi, usergroupApi } from "@/services/api";
import StarRating from "@/components/starRating/StarRating.vue";
import MaskedNumberInput from "@/components/common/MaskedNumberInput.vue";
import { useUserStore } from "@/config/store/userStore";
import { useApplicationStore } from "@/config/store/applicationStore";
import { hasApiErrorToast } from "@/services/application/apiErrorToast";
import { deepToRaw } from "@/services/application";
import { useInfinityGroups } from "@/services/usergroup";
import Combobox from "@/components/common/Combobox.vue";
import { useI18n } from "vue-i18n";
import { displayLocale } from "@/services/application/displayLocale";
import { motion } from "motion-v";
import { modalListAnimation } from "@/services/application/motion";
import {
  defaultRecruitmentCandidateProfileFieldKeys,
  resolveRecruitmentConfiguredFieldKeys,
  type TRecruitmentCandidateProfileFieldKey,
} from "@/services/recruitment/candidateCompletion";

const { t } = useI18n();
const props = defineProps<{
  modalOpen: boolean;
  processId: string | null;
}>();

const emits = defineEmits<{
  "update:modalOpen": [value: boolean];
  saved: [processId: string];
}>();

const userStore = useUserStore();
const applicationStore = useApplicationStore();

const {
  getMoreData: getMoreGroup,
  haveMore: haveMoreGroup,
  list: groupList,
  isLoading: isLoadingGroup,
  search: searchGroup,
  limit: limitGroup,
  page: pageGroup,
} = useInfinityGroups({});

const newSelectedImage = ref(undefined);
const dataRetentionValues = ref([
  {
    _id: 6,
    display: t("modules.recruitment.screening.editCandidateInformations.optionalFields.months", {
      count: 6,
    }),
  },
  {
    _id: 12,
    display: t("modules.recruitment.screening.editCandidateInformations.optionalFields.months", {
      count: 12,
    }),
  },
  {
    _id: 24,
    display: t("modules.recruitment.screening.editCandidateInformations.optionalFields.months", {
      count: 24,
    }),
  },
  {
    _id: 36,
    display: t("modules.recruitment.screening.editCandidateInformations.optionalFields.months", {
      count: 36,
    }),
  },
]);

const recruitmentProcess = ref(null);
const isLoadingProcess = ref(false);
const processLoadingFailed = ref(false);

// Consent given by the candidate on the job board form: read-only, it keeps the data 5 years
const extendedDataRetention = computed(
  () => recruitmentProcess.value?.candidate?.extendedDataRetention || null,
);
const dataRetentionOptions = computed(() =>
  extendedDataRetention.value?.accepted
    ? [
        ...dataRetentionValues.value,
        {
          _id: 60,
          display: t(
            "modules.recruitment.screening.editCandidateInformations.optionalFields.months",
            { count: 60 },
          ),
        },
      ]
    : dataRetentionValues.value,
);
const extendedDataRetentionLabel = computed(() => {
  const acceptedDate = extendedDataRetention.value?.acceptedDate;
  if (!extendedDataRetention.value?.accepted || !acceptedDate)
    return t(
      "modules.recruitment.screening.editCandidateInformations.optionalFields.extendedDataRetention",
    );
  return t(
    "modules.recruitment.screening.editCandidateInformations.optionalFields.extendedDataRetentionAccepted",
    { date: new Date(acceptedDate).toLocaleDateString(displayLocale.value) },
  );
});

const recruitmentProcessCopy = ref(null);
const candidateCopy = ref(null);

const updateCandidateCopy = () => {
  candidateCopy.value = structuredClone(deepToRaw(recruitmentProcess.value.candidate));
};

const updateProcessCopy = () => {
  recruitmentProcessCopy.value = structuredClone(deepToRaw(recruitmentProcess.value));
};

const getDegreeComboboxKey = (item) => {
  return item?.schoolId || item?.school?._id || item?._id || item?.tempId || null;
};

const getDegreeIdentityKey = (item) => {
  return item?._id || item?.tempId || getDegreeComboboxKey(item);
};

watch(
  () => props.modalOpen,
  (value) => {
    if (!value) {
      recruitmentProcessCopy.value = null;
      recruitmentProcess.value = null;
      candidateCopy.value = null;
      processLoadingFailed.value = false;
    }
  },
);

const loadRecruitmentProcess = async (processId: string) => {
  isLoadingProcess.value = true;
  processLoadingFailed.value = false;

  try {
    const response = await recruitmentApi.getProcessEditCandidateInformations(processId).promise;
    recruitmentProcess.value = response;
    updateProcessCopy();
    updateCandidateCopy();
  } catch (error) {
    recruitmentProcess.value = null;
    processLoadingFailed.value = true;
    applicationStore.pushError({
      title: t("modules.recruitment.screening.editCandidateInformations.loadingError"),
      color: "error",
    });
  } finally {
    isLoadingProcess.value = false;
  }
};

watch(
  () => [props.modalOpen, props.processId],
  async ([isOpen, processId]) => {
    if (!isOpen || !processId) return;

    await loadRecruitmentProcess(processId);
  },
  { immediate: true },
);

const degreeValues = computed(() => {
  return (
    recruitmentProcess.value?.candidate.degrees.map((degree) => {
      return {
        ...degree,
        display: degree.degree || degree.school?.display,
        comboboxKey: getDegreeComboboxKey(degree),
      };
    }) || []
  );
});

const schoolOptions = computed(() => {
  return schoolList.value.map((school) => {
    return {
      ...school,
      comboboxKey: getDegreeComboboxKey(school),
    };
  });
});

const selectDegree = (schools) => {
  const currentDegrees = recruitmentProcess.value.candidate.degrees;

  recruitmentProcess.value.candidate.degrees = schools.map((school) => {
    const existingDegree = currentDegrees.find(
      (degree) => getDegreeComboboxKey(degree) === getDegreeComboboxKey(school),
    );

    if (existingDegree) return existingDegree;

    const tempId = Math.random().toString(36).substring(7);

    return {
      tempId,
      schoolId: school.schoolId || school.school?._id || school._id || null,
      school: school.school || school,
      degree: school.degree || "",
      isMain: schools.length === 1,
    };
  });
};

const selectDegreeAsMain = (value, degree) => {
  recruitmentProcess.value.candidate.degrees = recruitmentProcess.value.candidate.degrees.map(
    (d) => {
      if (getDegreeIdentityKey(d) === getDegreeIdentityKey(degree)) d.isMain = value;
      else d.isMain = false;
      return d;
    },
  );
};

const removeDegree = (degree) => {
  const degreeIdentityKey = getDegreeIdentityKey(degree);

  recruitmentProcess.value.candidate.degrees = recruitmentProcess.value.candidate.degrees.filter(
    (d) => getDegreeIdentityKey(d) !== degreeIdentityKey,
  );
};

const {
  getMoreData: getMoreSchool,
  haveMore: haveMoreSchool,
  limit: limitSchool,
  page: pageSchool,
  list: schoolList,
  search: searchSchool,
  isLoading: isLoadingSchool,
} = useInfinitySchool({});

const {
  getMoreData: getMoreJob,
  haveMore: haveMoreJob,
  limit: limitJob,
  page: pageJob,
  list: jobList,
  search: searchJob,
  isLoading: isLoadingJob,
} = useInfinityJob({});

const {
  getMoreData: getMoreJobCategory,
  haveMore: haveMoreJobCategory,
  limit: limitJobCategory,
  page: pageJobCategory,
  list: jobCategoryList,
  search: searchJobCategory,
  isLoading: isLoadingJobCategory,
} = useInfinityJobCategory({});

const {
  getMoreData: getMoreContractType,
  haveMore: haveMoreContractType,
  limit: limitContractType,
  page: pageContractType,
  list: contractTypeList,
  search: searchContractType,
  isLoading: isLoadingContractType,
} = useInfinityContractType({});

const {
  getMoreData: getMoreTeam,
  haveMore: haveMoreTeam,
  limit: limitTeam,
  page: pageTeam,
  list: teamList,
  search: searchTeam,
  isLoading: isLoadingTeam,
} = useInfinityTeam({});

const {
  getMoreData: getMoreFormular,
  haveMore: haveMoreFormular,
  limit: limitFormular,
  page: pageFormular,
  list: listFormular,
  search: searchFormular,
  isLoading: isLoadingFormular,
} = useInfinityFormular({ moduleId: 4 });

const {
  getMoreData: getMoreOffice,
  haveMore: haveMoreOffice,
  limit: limitOffice,
  page: pageOffice,
  list: officeList,
  search: searchOffice,
  isLoading: isLoadingOffice,
} = useInfinityOffice({});

const {
  getMoreData: getMoreTag,
  haveMore: haveMoreTag,
  limit: limitTag,
  page: pageTag,
  list: tagList,
  search: searchTag,
  isLoading: isLoadingTag,
} = useInfinityRecruitmentTag({});

const items = computed(() => [
  {
    _id: 0,
    display: t("modules.recruitment.screening.editCandidateInformations.candidateInformation.man"),
    selected: recruitmentProcess.value?.candidate.gender === 0,
    icon: IonMdMan,
  },
  {
    _id: 1,
    display: t(
      "modules.recruitment.screening.editCandidateInformations.candidateInformation.woman",
    ),
    selected: recruitmentProcess.value?.candidate.gender === 1,
    icon: IonMdWoman,
  },
]);

const fullName = computed(() => {
  return `${recruitmentProcess.value?.candidate.firstname || ""} ${recruitmentProcess.value?.candidate.lastname || ""}`;
});

const picture = computed(() => {
  return recruitmentProcess.value?.candidate.picture || DefaultProfile;
});

const modalWidthClass = computed(() => {
  return "w-full md:w-[780px] xl:w-[1080px]";
});

const retreiveCroppedImage = ({ dataUrl }) => {
  newSelectedImage.value = undefined;
  recruitmentProcess.value.candidate.picture = dataUrl;
  recruitmentProcess.value.candidate.base64Picture = dataUrl;
};

const priority = computed({
  get: () => {
    if (recruitmentProcess.value.priority === 1) return 3;
    if (recruitmentProcess.value.priority === 3) return 1;
    return recruitmentProcess.value.priority;
  },
  set: (value) => {
    if (value === 1) recruitmentProcess.value.priority = 3;
    else if (value === 3) recruitmentProcess.value.priority = 1;
    else recruitmentProcess.value.priority = value;
  },
});

const getSelectedId = (selected) => {
  if (selected === null || selected === undefined || selected === "") return null;

  if (typeof selected === "object") return selected._id || null;

  return selected;
};

const getSelectedIds = (selectedList) => {
  if (!Array.isArray(selectedList)) return [];

  return selectedList.map((item) => getSelectedId(item)).filter(Boolean);
};

const getSelectedCountryId = (office) => {
  if (office === null || office === undefined) return null;

  return office?.countryId || null;
};

const updateProcessIds = (process) => {
  const { contractTypePopulated, ...restProcess } = process;

  return {
    ...restProcess,
    officeId: getSelectedId(process.office),
    countryId: getSelectedCountryId(process.office),
    // 0 is a source (spontaneous application by email): getSelectedId would turn it into null
    candidateSource: process.candidateSourcePopulated?._id ?? null,
    teamId: getSelectedId(process.team),
    contractType: getSelectedId(contractTypePopulated),
    candidate: {
      ...process.candidate,
      officeId: getSelectedId(process.office),
      countryId: getSelectedCountryId(process.office),
      degrees: process.candidate.degrees.map((degree) => {
        return {
          ...degree,
          _id: degree.tempId ? undefined : degree._id,
          schoolId: degree.schoolId || degree._id,
        };
      }),
      userGroups: getSelectedIds(process.candidate.userGroupsPopulated),
    },
    recommendedBy: {
      ...process.recommendedBy,
      website: getSelectedId(process.recommendedBy?.websitePopulated),
      resourceId: getSelectedId(process.recommendedBy?.resource),
      companyId: getSelectedId(process.recommendedBy?.company),
      degreeId: getSelectedId(process.recommendedBy?.degree),
    },
    jobCategoryId: getSelectedId(process.jobCategory),
    positionId: getSelectedId(process.position),
    evaluationForm: getSelectedId(process.evaluationFormPopulated),
    tags: getSelectedIds(process.tagsPopulated),
  };
};

const getCandidateUpdatedValues = (candidate, oldCandidate) => {
  return {
    _id: candidate._id,
    ...(candidate.firstname !== oldCandidate.firstname ? { firstname: candidate.firstname } : {}),
    ...(candidate.lastname !== oldCandidate.lastname ? { lastname: candidate.lastname } : {}),
    ...(candidate.phoneNumber1 !== oldCandidate.phoneNumber1
      ? { phoneNumber1: candidate.phoneNumber1 }
      : {}),
    ...(candidate.gender !== oldCandidate.gender ? { gender: candidate.gender } : {}),
    ...(JSON.stringify(candidate.degrees) !== JSON.stringify(oldCandidate.degrees)
      ? { degrees: candidate.degrees }
      : {}),
    ...(candidate.dataDuration !== oldCandidate.dataDuration
      ? { dataDuration: candidate.dataDuration }
      : {}),
    ...(candidate.recruitmentDate !== oldCandidate.recruitmentDate
      ? { recruitmentDate: candidate.recruitmentDate }
      : {}),
    ...(candidate.officeId !== oldCandidate.officeId ? { officeId: candidate.officeId } : {}),
    ...(candidate.candidateSource !== oldCandidate.candidateSource
      ? { candidateSource: candidate.candidateSource }
      : {}),
    ...(candidate.resourceType !== oldCandidate.resourceType
      ? { resourceType: candidate.resourceType }
      : {}),
    ...(JSON.stringify(candidate.userGroups) !== JSON.stringify(oldCandidate.userGroups)
      ? { userGroups: candidate.userGroups }
      : {}),
    invitedBy: candidate.invitedBy,
    authorId: candidate.authorId,
  };
};

const updateCandidate = async () => {
  if (!recruitmentProcess.value) return;

  const processUpdated = updateProcessIds(recruitmentProcess.value);

  const { candidate, ...process } = processUpdated;
  const candidateToUpdate = getCandidateUpdatedValues(candidate, candidateCopy.value);
  const { __v, ...processToUpdated } = process;
  // return

  if (!candidateToUpdate.invitedBy) candidateToUpdate.invitedBy = userStore.user._id;
  if (!candidateToUpdate.authorId) candidateToUpdate.authorId = userStore.user._id;
  const emptyPromise = { promise: new Promise((resolve) => resolve("empty")) };
  const userRequest =
    recruitmentProcess.value.candidate.user.email ===
    recruitmentProcessCopy.value.candidate.user.email
      ? emptyPromise
      : userApi.updateEmail(recruitmentProcess.value.candidate.userId, {
          email: recruitmentProcess.value.candidate.user.email,
        });
  userRequest.promise
    .then((resolvedBy) => {
      if (resolvedBy === "empty") return;
      applicationStore.pushError({
        title: t(
          "modules.recruitment.screening.editCandidateInformations.candidateInformation.emailUpdate",
        ),
        color: "success",
      });
    })
    .catch((error) => {
      // An email of another fiche of the firm: its own toast (axios.config) says so
      if (hasApiErrorToast(error)) return;
      applicationStore.pushError({
        title: t(
          "modules.recruitment.screening.editCandidateInformations.candidateInformation.emailUpdate",
        ),
        message: t(
          "modules.recruitment.screening.editCandidateInformations.candidateInformation.emailVerification",
        ),
        color: "error",
      });
    });
  // if (candidate.base64Picture) {
  // }
  const pictureUpdateRequest = candidate.base64Picture
    ? resourceApi.updateResourcePicture(candidate._id, { base64: candidate.base64Picture })
    : emptyPromise;
  pictureUpdateRequest.promise
    .then((resolvedBy) => {
      if (resolvedBy === "empty") return;

      recruitmentProcess.value.candidate.picture = resolvedBy.picture;
      applicationStore.pushError({
        title: t(
          "modules.recruitment.screening.editCandidateInformations.candidateInformation.pictureUpdate",
        ),
        color: "success",
      });
    })
    .catch(() => {
      applicationStore.pushError({
        title: t(
          "modules.recruitment.screening.editCandidateInformations.candidateInformation.pictureUpdateError",
        ),
        color: "error",
      });
    });
  const updateResourceRequest = resourceApi.partialUpdate(candidateToUpdate);

  updateResourceRequest.promise
    .then((resource) => {
      applicationStore.pushError({
        title: t(
          "modules.recruitment.screening.editCandidateInformations.candidateInformation.informationUpdate",
        ),
        color: "success",
      });
    })
    .catch(() => {
      applicationStore.pushError({
        title: t(
          "modules.recruitment.screening.editCandidateInformations.candidateInformation.InformationUpdateError",
        ),
        color: "error",
      });
    });

  const updateProcessRequest = recruitmentApi.updateRecruitmentProcess(processToUpdated);
  updateProcessRequest.promise
    .then((process) => {
      applicationStore.pushError({
        title: t(
          "modules.recruitment.screening.editCandidateInformations.candidateInformation.recruitmentProcessUpdate",
        ),
        color: "success",
      });
    })
    .catch(() => {
      applicationStore.pushError({
        title: t(
          "modules.recruitment.screening.editCandidateInformations.candidateInformation.recruitmentProcessUpdateError",
        ),
        color: "error",
      });
    });

  const results = await Promise.allSettled([
    userRequest.promise,
    pictureUpdateRequest.promise,
    updateResourceRequest.promise,
    updateProcessRequest.promise,
  ]);

  if (results.some((result) => result.status === "rejected")) return;

  const processId = recruitmentProcess.value._id;
  emits("update:modalOpen", false);
  emits("saved", processId);
};

const cancelEdit = async () => {
  emits("update:modalOpen", false);
};

const escapeHtml = (str: string) => {
  if (!str) return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const isListedSchoolDegree = (degree: any) => !!(degree?.school || degree?.schoolId);

const getDegreeTooltip = (degree: any) => {
  const lines = [];
  if (isListedSchoolDegree(degree))
    lines.push(escapeHtml(t("components.resource.degree.degreeItem.listedInstitution")));
  else lines.push(escapeHtml(t("components.resource.degree.degreeItem.unlistedInstitution")));
  if (degree.isMain)
    lines.push(
      `<span style="font-size:1.2em">⭐</span> ${escapeHtml(t("components.resource.degree.degreeItem.isMain"))}`,
    );
  if (degree.school?.isTargetSchool)
    lines.push(
      `<span style="font-size:1.2em">🥇</span> ${escapeHtml(t("components.resource.degree.degreeItem.targetSchool"))}`,
    );
  if (degree.category?.display)
    lines.push(`<span style="font-size:1.2em">🎓</span> ${escapeHtml(degree.category.display)}`);
  if (degree.school?.display)
    lines.push(`<span style="font-size:1.2em">🏫</span> ${escapeHtml(degree.school.display)}`);
  else if (degree.degree)
    lines.push(`<span style="font-size:1.2em">✏️</span> ${escapeHtml(degree.degree)}`);
  if (degree.school?.category?.display)
    lines.push(`&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${escapeHtml(degree.school.category.display)}`);
  if (degree.startYear || degree.endYear) {
    const yearStr =
      degree.startYear && degree.endYear
        ? `${degree.startYear} - ${degree.endYear}`
        : degree.startYear
          ? `${degree.startYear}`
          : `- ${degree.endYear}`;
    lines.push(`<span style="font-size:1.2em">📅</span> ${yearStr}`);
  }
  if (degree.description)
    lines.push(`<span style="font-size:1.2em">📝</span> ${escapeHtml(degree.description)}`);
  return lines.join("<br>");
};

const updateOfficeSelect = (office) => {
  recruitmentProcess.value.countryId = office?.countryId || null;
};

const profileRequiredFieldKeys = computed(
  () =>
    new Set(
      resolveRecruitmentConfiguredFieldKeys(
        recruitmentProcess.value?.office?.options?.recruitment?.candidateProfileCompletionFields,
        defaultRecruitmentCandidateProfileFieldKeys,
        "profile",
      ) as TRecruitmentCandidateProfileFieldKey[],
    ),
);

const isFieldRequired = (fieldKey: TRecruitmentCandidateProfileFieldKey) =>
  profileRequiredFieldKeys.value.has(fieldKey);
</script>

<template>
  <BModal
    position="right"
    :model-value="props.modalOpen"
    :class="modalWidthClass"
    @update:model-value="emits('update:modalOpen', $event)"
  >
    <template #header>
      <h1 class="text-2xl">
        {{ t("modules.recruitment.screening.editCandidateInformations.title") }}
      </h1>
    </template>
    <template #default>
      <motion.div
        v-if="isLoadingProcess"
        class="flex flex-col gap-10 pb-[250px]"
        :variants="modalListAnimation.container"
        initial="hidden"
        animate="show"
      >
        <motion.div :variants="modalListAnimation.item" class="bg-fade p-4 rounded-xl shadow-inner">
          <div class="flex flex-wrap gap-8 items-start">
            <div class="flex flex-col gap-3 items-center relative grow max-w-[96px]">
              <Skeleton class="w-20 h-20 rounded-full!" />
              <Skeleton class="w-24 h-6" />
            </div>
            <div class="flex flex-col gap-4 grow-2 min-w-[220px]">
              <Skeleton class="w-36 h-5" />
              <Skeleton class="w-full h-11" />
              <Skeleton class="w-full h-11" />
            </div>
            <div class="flex flex-col gap-4 w-96 max-w-full grow shrink">
              <Skeleton class="w-28 h-5" />
              <Skeleton class="w-full h-11" />
              <Skeleton class="w-full h-11" />
            </div>
            <div class="flex flex-col gap-4 w-96 max-w-full grow">
              <Skeleton class="w-44 h-5" />
              <Skeleton class="w-full h-11" />
            </div>
          </div>
        </motion.div>

        <motion.div :variants="modalListAnimation.item" class="bg-fade p-4 rounded-xl shadow-inner">
          <div class="flex flex-col gap-6">
            <Skeleton class="w-40 h-6" />
            <div class="flex flex-wrap gap-4">
              <Skeleton class="w-full md:w-96 h-11" />
              <Skeleton class="w-full md:w-96 h-11" />
            </div>
            <div class="flex flex-wrap gap-4">
              <Skeleton class="w-full md:w-52 h-24" />
              <Skeleton class="w-full md:w-52 h-24" />
              <Skeleton class="w-full md:w-52 h-24" />
            </div>
          </div>
        </motion.div>

        <motion.div :variants="modalListAnimation.item" class="bg-fade p-4 rounded-xl shadow-inner">
          <div class="flex flex-col gap-6">
            <Skeleton class="w-44 h-6" />
            <div class="flex flex-wrap gap-6">
              <div class="flex flex-col gap-4 grow min-w-[220px]">
                <Skeleton class="w-full h-11" />
                <Skeleton class="w-full h-11" />
                <Skeleton class="w-full h-11" />
              </div>
              <div class="flex flex-col gap-4 grow min-w-[220px]">
                <Skeleton class="w-full h-11" />
                <Skeleton class="w-full h-11" />
              </div>
              <div class="flex flex-col gap-4 grow min-w-[220px]">
                <Skeleton class="w-full h-11" />
                <Skeleton class="w-full h-11" />
                <Skeleton class="w-full h-11" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
      <div
        v-else-if="processLoadingFailed"
        class="flex min-h-40 items-center justify-center subtitle"
      >
        {{ t("modules.recruitment.screening.editCandidateInformations.loadingError") }}
      </div>
      <motion.div
        v-else-if="recruitmentProcess"
        :key="recruitmentProcess._id"
        class="flex flex-col gap-10 pb-[250px]"
        :variants="modalListAnimation.container"
        initial="hidden"
        animate="show"
      >
        <motion.div :variants="modalListAnimation.item" class="flex flex-col gap-4">
          <div class="bg-fade p-4 rounded-xl shadow-inner">
            <h1 class="text-xl pb-8">
              {{
                t(
                  "modules.recruitment.screening.editCandidateInformations.recruitmentProcess.selectCandidateSource.title",
                )
              }}
            </h1>
            <div class="flex flex-wrap gap-8">
              <div class="flex flex-col gap-2 items-center relative grow">
                <p v-if="isFieldRequired('picture')" class="subtitle text-sm">
                  {{ t("modules.recruitment.createCandidate.optionalInformation.picture") }}
                  <span class="text-danger-400" aria-hidden="true">*</span>
                </p>
                <img
                  v-if="picture"
                  :src="picture"
                  alt="profile picture"
                  class="rounded-full w-20 h-20"
                />
                <DefaultProfile v-else class="w-20 h-20" />
                <SearchImageOnGoogle
                  :search="fullName"
                  @selected-image="newSelectedImage = $event"
                />
                <Cropper :image="newSelectedImage" @updated:image="retreiveCroppedImage($event)" />
              </div>
              <div class="flex flex-col gap-8 grow-2">
                <TextInput
                  v-model="recruitmentProcess.candidate.firstname"
                  container-class="grow"
                  :label="
                    t(
                      'modules.recruitment.screening.editCandidateInformations.candidateInformation.firstname',
                    )
                  "
                  :required="isFieldRequired('firstname')"
                />
                <TextInput
                  v-model="recruitmentProcess.candidate.lastname"
                  container-class="grow"
                  :label="
                    t(
                      'modules.recruitment.screening.editCandidateInformations.candidateInformation.lastname',
                    )
                  "
                  :required="isFieldRequired('lastname')"
                />
              </div>
              <div class="flex flex-col gap-2 w-96 grow shrink">
                <TextInput
                  v-model="recruitmentProcess.candidate.user.email"
                  container-class="grow"
                  :label="
                    t(
                      'modules.recruitment.screening.editCandidateInformations.candidateInformation.email',
                    )
                  "
                  :required="isFieldRequired('email')"
                />
                <p v-if="isFieldRequired('gender')" class="subtitle text-sm">
                  {{ t("modules.recruitment.recommandation.mandatoryInformation.gender") }}
                  <span class="text-danger-400" aria-hidden="true">*</span>
                </p>
                <BChoiceOptions
                  :items="items"
                  @update:items="recruitmentProcess.candidate.gender = $event._id"
                />
              </div>
              <Combobox
                :model-value="degreeValues"
                @update:model-value="selectDegree"
                v-model:search="searchSchool"
                v-model:limit="limitSchool"
                v-model:page="pageSchool"
                v-model:have-more="haveMoreSchool"
                :loading="isLoadingSchool"
                :options="schoolOptions"
                by="comboboxKey"
                :label="
                  $t(
                    'modules.recruitment.screening.editCandidateInformations.candidateInformation.academicInstitution',
                  )
                "
                placeholder="HEC"
                class="w-96! grow"
                :required="isFieldRequired('degrees')"
                @search="getMoreSchool"
              >
                <template #selected-render="{ selected }">
                  <b-label
                    size="dense"
                    class="cursor-pointer"
                    :bg-color="isListedSchoolDegree(selected) ? 'primary' : 'warning'"
                    v-tooltip="{ content: getDegreeTooltip(selected), html: true }"
                    @click.prevent="removeDegree(selected)"
                  >
                    <SolarMapPointSchoolLineDuotone
                      v-if="isListedSchoolDegree(selected)"
                      class="size-4 shrink-0"
                    />
                    <SolarPenBroken v-else class="size-4 shrink-0" />
                    <span :class="{ underline: !isListedSchoolDegree(selected) }">
                      {{ selected.school?.display || selected.degree }}
                    </span>
                    <TargetSchoolIcon
                      v-if="selected.school?.isTargetSchool || selected.isTargetSchool"
                    />
                    <b-checkbox
                      :model-value="selected.isMain"
                      label="Main"
                      @update:model-value="selectDegreeAsMain($event, selected)"
                    />
                  </b-label>
                </template>
                <template #custom-item="{ item }">
                  <span class="flex min-w-0 items-center gap-1.5">
                    <TargetSchoolIcon v-if="item.isTargetSchool" />
                    <span class="truncate">{{ item.display }}</span>
                  </span>
                </template>
              </Combobox>
            </div>
          </div>
        </motion.div>

        <motion.div :variants="modalListAnimation.item" class="flex flex-col gap-4">
          <div class="bg-fade p-4 rounded-xl shadow-inner">
            <h1 class="text-xl pb-8">
              {{
                t(
                  "modules.recruitment.screening.editCandidateInformations.recruitmentProcess.title",
                )
              }}
            </h1>
            <div class="flex flex-col flex-wrap gap-8 grow shrink">
              <div class="flex flex-col gap-4">
                <SelectCandidateSource
                  v-model="recruitmentProcess"
                  :required="isFieldRequired('source')"
                  with-offer
                />
                <hr />
                <!-- <BAutocompleteInfinity
                  v-model="recruitmentProcess.position" :placeholder="t('modules.recruitment.screening.editCandidateInformations.recruitmentProcess.targetPositionPlaceholder')" :label="t('modules.recruitment.screening.editCandidateInformations.recruitmentProcess.targetPosition')"
                  :have-more="jobHaveMore" class="w-full" :list="jobList" close-after @get-more-data="jobGetMoreData"
                /> -->

                <Combobox
                  v-model="recruitmentProcess.jobCategory"
                  v-model:search="searchJobCategory"
                  v-model:limit="limitJobCategory"
                  v-model:page="pageJobCategory"
                  v-model:have-more="haveMoreJobCategory"
                  class="w-full md:w-96"
                  :loading="isLoadingJobCategory"
                  :label="t('modules.recruitment.createCandidate.mandatoryInformation.jobCategory')"
                  :placeholder="
                    t('modules.recruitment.createCandidate.mandatoryInformation.jobCategoryPlaceholder')
                  "
                  :options="jobCategoryList"
                  @search="getMoreJobCategory"
                  :required="isFieldRequired('jobCategory')"
                  data-cy="candidate-job-category"
                />

                <Combobox
                  v-model="recruitmentProcess.position"
                  v-model:search="searchJob"
                  v-model:limit="limitJob"
                  v-model:page="pageJob"
                  v-model:have-more="haveMoreJob"
                  class="w-full md:w-96"
                  :loading="isLoadingJob"
                  :label="
                    t('modules.recruitment.createCandidate.mandatoryInformation.targetPosition')
                  "
                  :placeholder="
                    t(
                      'modules.recruitment.createCandidate.mandatoryInformation.targetPositionPlaceholder',
                    )
                  "
                  :options="jobList"
                  @search="getMoreJob"
                  :required="isFieldRequired('position')"
                  data-cy="candidate-position"
                />

                <Combobox
                  v-model="recruitmentProcess.contractTypePopulated"
                  v-model:search="searchContractType"
                  v-model:limit="limitContractType"
                  v-model:page="pageContractType"
                  v-model:have-more="haveMoreContractType"
                  :loading="isLoadingContractType"
                  :options="contractTypeList"
                  :label="
                    t(
                      'modules.recruitment.screening.editCandidateInformations.recruitmentProcess.targetContract',
                    )
                  "
                  :placeholder="
                    t(
                      'modules.recruitment.screening.editCandidateInformations.recruitmentProcess.targetContractPlaceholder',
                    )
                  "
                  class="w-full md:w-96"
                  :required="isFieldRequired('contractType')"
                  @search="getMoreContractType"
                >
                  <template #custom-item="{ item }">
                    <span class="flex items-center gap-2">
                      <CountryFlag :country="item.countryId" />
                      {{ item.display }}
                    </span>
                  </template>
                  <template #selected-render="{ selected }">
                    <span class="flex items-center gap-2">
                      <CountryFlag :country="selected.countryId" />
                      {{ selected.display }}
                    </span>
                  </template>
                </Combobox>
              </div>

              <div class="flex flex-row flex-wrap gap-8 grow shrink">
                <div class="flex flex-col gap-1 w-52 grow">
                  <div
                    class="flex flex-col gap-2 border-2 border-base p-4 rounded-xl"
                    :class="{
                      'border-success-300 dark:border-success-800':
                        !recruitmentProcess.evaluationFormPopulated,
                    }"
                  >
                    <h2>
                      {{
                        t(
                          "modules.recruitment.screening.editCandidateInformations.recruitmentProcess.minimalEvaluation",
                        )
                      }}
                      <span class="italic text-sm subtitle">
                        {{
                          t(
                            "modules.recruitment.screening.editCandidateInformations.recruitmentProcess.minimalEvaluationPlaceholder",
                          )
                        }}
                      </span>
                    </h2>
                  </div>
                  <p class="mx-auto">
                    {{
                      t(
                        "modules.recruitment.screening.editCandidateInformations.recruitmentProcess.or",
                      )
                    }}
                  </p>
                  <Combobox
                    v-model="recruitmentProcess.evaluationFormPopulated"
                    v-model:search="searchFormular"
                    v-model:limit="limitFormular"
                    v-model:page="pageFormular"
                    v-model:have-more="haveMoreFormular"
                    :loading="isLoadingFormular"
                    :options="listFormular"
                    :label="
                      t(
                        'modules.recruitment.screening.editCandidateInformations.recruitmentProcess.evaluationType',
                      )
                    "
                    :placeholder="
                      t(
                        'modules.recruitment.screening.editCandidateInformations.recruitmentProcess.evaluationTypePlaceholder',
                      )
                    "
                    class="w-full"
                    :required="isFieldRequired('evaluationForm')"
                    @search="getMoreFormular"
                  />
                </div>
                <div class="flex flex-col gap-7 w-52 grow">
                  <div class="flex gap-7">
                    <div class="flex flex-col gap-1">
                      <p>
                        {{
                          t(
                            "modules.recruitment.screening.editCandidateInformations.recruitmentProcess.availabilityDate",
                          )
                        }}
                        <span
                          v-if="isFieldRequired('availabilityDate')"
                          class="text-danger-400"
                          aria-hidden="true"
                          >*</span
                        >
                      </p>
                      <b-date-picker
                        v-model="recruitmentProcess.candidate.recruitmentDate"
                        label="Date"
                        :required="isFieldRequired('availabilityDate')"
                      />
                    </div>
                    <div class="flex flex-col gap-1">
                      <p>
                        {{
                          t(
                            "modules.recruitment.screening.editCandidateInformations.recruitmentProcess.plannedContractStartDate",
                          )
                        }}
                        <span
                          v-if="isFieldRequired('plannedContractStartDate')"
                          class="text-danger-400"
                          aria-hidden="true"
                          >*</span
                        >
                      </p>
                      <b-date-picker
                        allow-clear
                        v-model="recruitmentProcess.plannedContractStartDate"
                        label="Date"
                        :required="isFieldRequired('plannedContractStartDate')"
                      />
                    </div>
                  </div>
                  <Combobox
                    v-model="recruitmentProcess.office"
                    v-model:search="searchOffice"
                    v-model:limit="limitOffice"
                    v-model:page="pageOffice"
                    v-model:have-more="haveMoreOffice"
                    :loading="isLoadingOffice"
                    :options="officeList"
                    :label="
                      t(
                        'modules.recruitment.screening.editCandidateInformations.recruitmentProcess.location',
                      )
                    "
                    :placeholder="
                      t(
                        'modules.recruitment.screening.editCandidateInformations.recruitmentProcess.locationPlaceholder',
                      )
                    "
                    class="w-full"
                    :required="isFieldRequired('office')"
                    @search="getMoreOffice"
                    @update:model-value="updateOfficeSelect"
                  >
                    <template #custom-item="{ item }">
                      <CountryFlag :country="item.countryId" />
                      {{ item.display }}
                    </template>
                    <template #selected-render="{ selected }">
                      <span class="flex items-center gap-1">
                        <CountryFlag :country="selected.countryId" />
                        {{ selected.display }}
                      </span>
                    </template>
                  </Combobox>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div :variants="modalListAnimation.item" class="flex flex-col gap-4">
          <div class="bg-fade p-4 rounded-xl shadow-inner">
            <h1 class="text-xl pb-8">
              {{
                t("modules.recruitment.screening.editCandidateInformations.optionalFields.title")
              }}
            </h1>
            <div class="flex flex-wrap gap-8 grow shrink items-start">
              <div class="flex flex-col flex-wrap gap-8 grow">
                <Combobox
                  v-model="recruitmentProcess.candidate.userGroupsPopulated"
                  :options="groupList"
                  v-model:have-more="haveMoreGroup"
                  v-model:search="searchGroup"
                  v-model:page="pageGroup"
                  v-model:limit="limitGroup"
                  :loading="isLoadingGroup"
                  :label="
                    t(
                      'modules.recruitment.screening.editCandidateInformations.optionalFields.speciality',
                    )
                  "
                  class="w-96 max-w-full"
                  :placeholder="
                    t(
                      'modules.recruitment.screening.editCandidateInformations.optionalFields.specialityPlaceholder',
                    )
                  "
                  :required="isFieldRequired('group')"
                  @search="getMoreGroup"
                />
                <Combobox
                  v-model="recruitmentProcess.tagsPopulated"
                  v-model:search="searchTag"
                  v-model:limit="limitTag"
                  v-model:page="pageTag"
                  v-model:have-more="haveMoreTag"
                  :loading="isLoadingTag"
                  :options="tagList"
                  :label="t('modules.recruitment.tags.label')"
                  :placeholder="t('modules.recruitment.tags.placeholder')"
                  class="w-96 max-w-full"
                  :required="isFieldRequired('tags')"
                  @search="getMoreTag"
                />
                <Combobox
                  v-model="recruitmentProcess.team"
                  v-model:search="searchTeam"
                  v-model:limit="limitTeam"
                  v-model:page="pageTeam"
                  v-model:have-more="haveMoreTeam"
                  :loading="isLoadingTeam"
                  :options="teamList"
                  :label="t('components.recruitment.interviews.candidateInformation.team')"
                  :placeholder="
                    t(
                      'modules.recruitment.screening.editCandidateInformations.optionalFields.desiredTeam',
                    )
                  "
                  class="w-full"
                  :required="isFieldRequired('team')"
                  @search="getMoreTeam"
                />
              </div>
              <div class="flex flex-col flex-wrap gap-8 grow">
                <div class="flex flex-col gap-2">
                  <b-select
                    v-model="recruitmentProcess.candidate.dataDuration"
                    class="grow"
                    :options="dataRetentionOptions"
                    :disabled="Boolean(extendedDataRetention?.accepted)"
                    :label="
                      t(
                        'modules.recruitment.screening.editCandidateInformations.optionalFields.dataRetention',
                      )
                    "
                    :required="isFieldRequired('dataDuration')"
                  />
                  <BAlert
                    v-if="extendedDataRetention?.accepted"
                    :alert="{ duration: 0, title: extendedDataRetentionLabel, color: 'success-op' }"
                    :show-close="false"
                    size="small"
                    data-cy="candidate-extended-data-retention"
                  >
                    <template #icon>
                      <MaterialSymbolsVerifiedUserOutlineRounded class="h-5 w-5" />
                    </template>
                  </BAlert>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="subtitle text-sm" for="isEvaluation-confidential"
                    >{{
                      t(
                        "modules.recruitment.screening.editCandidateInformations.optionalFields.evaluationVisibility",
                      )
                    }}<span
                      v-if="isFieldRequired('isConfidential')"
                      class="text-danger-400"
                      aria-hidden="true"
                      >*</span
                    ></label
                  >
                  <BRadio
                    id="isEvaluation-confidential"
                    v-model="recruitmentProcess.isConfidential"
                    :label="
                      t(
                        'modules.recruitment.screening.editCandidateInformations.optionalFields.evaluationVisibilityChoose',
                      )
                    "
                  />
                </div>
              </div>
              <div class="flex flex-col flex-wrap gap-8">
                <MaskedNumberInput
                  v-model="recruitmentProcess.candidate.phoneNumber1"
                  :default-country="recruitmentProcess.countryId"
                  :label="
                    t(
                      'modules.recruitment.screening.editCandidateInformations.optionalFields.phoneNumber',
                    )
                  "
                  :required="isFieldRequired('phoneNumber1')"
                />
                <StarRating
                  v-model:rating="priority"
                  :label="
                    t(
                      'modules.recruitment.screening.editCandidateInformations.optionalFields.priority',
                    )
                  "
                  :max-rating="3"
                  clearable
                  :required="isFieldRequired('priority')"
                />
                <BRadios
                  v-model:selectedOption="recruitmentProcess.candidate.resourceType"
                  :label="
                    t(
                      'modules.recruitment.screening.editCandidateInformations.optionalFields.resourceType',
                    )
                  "
                  :required="isFieldRequired('resourceType')"
                  :options="[
                    {
                      value: 0,
                      label: t(
                        'modules.recruitment.screening.editCandidateInformations.optionalFields.internal',
                      ),
                    },
                    {
                      value: 1,
                      label: t(
                        'modules.recruitment.screening.editCandidateInformations.optionalFields.external',
                      ),
                    },
                  ]"
                />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </template>
    <template #footer>
      <div class="flex justify-end gap-5">
        <BButton bg="error" @click="cancelEdit">
          {{ t("common.cancel") }}
        </BButton>
        <BButton :disabled="!recruitmentProcess || isLoadingProcess" @click="updateCandidate">
          {{ t("common.update") }}
        </BButton>
      </div>
    </template>
  </BModal>
</template>
